/**
 * Performance Utility Classes
 *
 * Provides reusable utilities for performance optimization:
 * - ObjectPool: Resource pooling for Three.js objects
 * - RollingAverage: Smooth metrics calculation
 * - DeviceCapabilityDetector: GPU tier detection
 *
 * @module utils/performance-utils
 */

/**
 * Object Pool for Three.js Resource Management
 *
 * Reduces garbage collection overhead by reusing objects.
 * Suitable for geometries, materials, and other frequently allocated resources.
 */
export class ObjectPool {
  /**
   * @param {Function} factory - Function that creates new instances
   * @param {number} initialSize - Pre-allocated pool size
   */
  constructor(factory, initialSize = 5) {
    this.factory = factory;
    this.available = [];
    this.inUse = new Set();

    // Pre-allocate objects
    for (let i = 0; i < initialSize; i++) {
      this.available.push(factory());
    }
  }

  /**
   * Acquire an object from the pool
   * @returns {*} Pooled object instance
   */
  acquire() {
    if (this.available.length === 0) {
      console.warn('[ObjectPool] Pool exhausted, creating new object');
      const obj = this.factory();
      this.inUse.add(obj);
      return obj;
    }

    const obj = this.available.pop();
    this.inUse.add(obj);
    return obj;
  }

  /**
   * Release an object back to the pool
   * @param {*} obj - Object to release
   */
  release(obj) {
    if (!this.inUse.has(obj)) {
      console.warn('[ObjectPool] Attempting to release object not in use');
      return;
    }

    this.inUse.delete(obj);
    this.available.push(obj);
  }

  /**
   * Get pool statistics
   * @returns {{available: number, inUse: number, total: number}}
   */
  getStats() {
    return {
      available: this.available.length,
      inUse: this.inUse.size,
      total: this.available.length + this.inUse.size,
    };
  }

  /**
   * Dispose all pooled objects (if they have dispose method)
   */
  dispose() {
    const allObjects = [...this.available, ...this.inUse];
    allObjects.forEach((obj) => {
      if (obj && typeof obj.dispose === 'function') {
        obj.dispose();
      }
    });
    this.available = [];
    this.inUse.clear();
  }
}

/**
 * Rolling Average Calculator
 *
 * Calculates moving average for smooth metrics (FPS, frame time, etc.)
 */
export class RollingAverage {
  /**
   * @param {number} windowSize - Number of samples to average
   */
  constructor(windowSize = 60) {
    this.windowSize = windowSize;
    this.samples = [];
    this.sum = 0;
  }

  /**
   * Add a new sample and get updated average
   * @param {number} value - New sample value
   * @returns {number} Current rolling average
   */
  add(value) {
    this.samples.push(value);
    this.sum += value;

    // Remove oldest sample if window exceeded
    if (this.samples.length > this.windowSize) {
      const oldest = this.samples.shift();
      this.sum -= oldest;
    }

    return this.getAverage();
  }

  /**
   * Get current average without adding a sample
   * @returns {number} Current rolling average
   */
  getAverage() {
    if (this.samples.length === 0) return 0;
    return this.sum / this.samples.length;
  }

  /**
   * Reset the rolling average
   */
  reset() {
    this.samples = [];
    this.sum = 0;
  }

  /**
   * Get number of samples currently in window
   * @returns {number} Sample count
   */
  getSampleCount() {
    return this.samples.length;
  }
}

/**
 * Device Capability Detector
 *
 * Detects GPU tier and device capabilities for adaptive quality settings.
 */
export class DeviceCapabilityDetector {
  /**
   * Detect device capabilities from WebGL context
   * @param {WebGLRenderingContext} gl - WebGL context
   * @returns {Object} Device capabilities
   */
  static detect(gl) {
    if (!gl) {
      console.warn('[DeviceCapabilityDetector] No WebGL context provided');
      return DeviceCapabilityDetector.getFallbackCapabilities();
    }

    const capabilities = {
      webglVersion: DeviceCapabilityDetector.getWebGLVersion(gl),
      maxAnisotropy: DeviceCapabilityDetector.getMaxAnisotropy(gl),
      maxTextureSize: gl.getParameter(gl.MAX_TEXTURE_SIZE),
      maxRenderBufferSize: gl.getParameter(gl.MAX_RENDERBUFFER_SIZE),
      maxVertexAttributes: gl.getParameter(gl.MAX_VERTEX_ATTRIBS),
      maxTextureUnits: gl.getParameter(gl.MAX_TEXTURE_IMAGE_UNITS),
      pixelRatio: window.devicePixelRatio || 1,
      userAgent: navigator.userAgent,
      platform: DeviceCapabilityDetector.getPlatform(),
    };

    // Determine GPU tier based on capabilities
    capabilities.gpuTier = DeviceCapabilityDetector.classifyGPUTier(capabilities);

    return capabilities;
  }

  /**
   * Get WebGL version
   * @param {WebGLRenderingContext} gl
   * @returns {number} 1 or 2
   */
  static getWebGLVersion(gl) {
    if (gl instanceof WebGL2RenderingContext) return 2;
    return 1;
  }

  /**
   * Get maximum anisotropic filtering level
   * @param {WebGLRenderingContext} gl
   * @returns {number} Max anisotropy level
   */
  static getMaxAnisotropy(gl) {
    const ext =
      gl.getExtension('EXT_texture_filter_anisotropic') ||
      gl.getExtension('MOZ_EXT_texture_filter_anisotropic') ||
      gl.getExtension('WEBKIT_EXT_texture_filter_anisotropic');

    if (ext) {
      return gl.getParameter(ext.MAX_TEXTURE_MAX_ANISOTROPY_EXT);
    }
    return 1; // No anisotropic filtering support
  }

  /**
   * Classify GPU tier based on capabilities
   * @param {Object} capabilities
   * @returns {string} 'high' | 'mid' | 'low'
   */
  static classifyGPUTier(capabilities) {
    let score = 0;

    // WebGL 2 support (+2 points)
    if (capabilities.webglVersion === 2) score += 2;

    // High anisotropy support (+2 points)
    if (capabilities.maxAnisotropy >= 16) score += 2;
    else if (capabilities.maxAnisotropy >= 8) score += 1;

    // Large texture support (+2 points)
    if (capabilities.maxTextureSize >= 8192) score += 2;
    else if (capabilities.maxTextureSize >= 4096) score += 1;

    // High pixel ratio devices (likely high-end) (+1 point)
    if (capabilities.pixelRatio >= 2) score += 1;

    // Classify based on total score (max 7 points)
    if (score >= 5) return 'high'; // 5-7 points
    if (score >= 3) return 'mid'; // 3-4 points
    return 'low'; // 0-2 points
  }

  /**
   * Detect platform (iOS, Android, desktop)
   * @returns {string} 'iOS' | 'Android' | 'desktop'
   */
  static getPlatform() {
    const ua = navigator.userAgent;
    if (/iPhone|iPad|iPod/i.test(ua)) return 'iOS';
    if (/Android/i.test(ua)) return 'Android';
    return 'desktop';
  }

  /**
   * Get fallback capabilities when WebGL is unavailable
   * @returns {Object} Minimal capabilities
   */
  static getFallbackCapabilities() {
    return {
      webglVersion: 1,
      maxAnisotropy: 1,
      maxTextureSize: 2048,
      maxRenderBufferSize: 2048,
      maxVertexAttributes: 16,
      maxTextureUnits: 8,
      pixelRatio: window.devicePixelRatio || 1,
      userAgent: navigator.userAgent,
      platform: DeviceCapabilityDetector.getPlatform(),
      gpuTier: 'low',
    };
  }
}
